'use client';

import DeleteChatDialog from '@/components/chat/components/DeleteChatDialog';
import { SignUpPromptModalChat } from '@/components/modal/sign-up-prompt-modal-chat';
import type { ChatListItem } from '@/types';
import { Button } from '@heidi/design-system/button';
import { Loader } from '@heidi/design-system/loader';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useIntl } from 'react-intl';
import { EvidenceChatItem } from './evidence-chat-item';
import { useEvidencePanelState } from './hooks/useEvidencePanelState';

interface EvidencePanelContentProps {
  /** Optional callback to close the containing drawer/sheet. If not provided, uses isMobileHeaderSheetOpenAtom. */
  onClose?: () => void;
}

/**
 * Evidence/Chat panel content for mobile sidebar and drawers.
 *
 * This is a copy of ChatHistoryOverlay for backwards compatibility.
 * ChatSidebar returns null on mobile (it expects ChatHistoryOverlay to be used),
 * so we duplicate the chat list UI here for the PageShell mobile panel context.
 *
 * Key differences from ChatHistoryOverlay:
 * - No full-page overlay wrapper (renders inline in mobile panel)
 * - Closes via onClose prop or isMobileHeaderSheetOpenAtom
 * - Analytics source is 'sidebar' instead of 'history_overlay'
 *
 * Note: ChatHistoryOverlay is deprecated and can be removed once this component
 * is confirmed stable in production.
 *
 * @see ChatHistoryOverlay - The original implementation this is based on
 * @see ChatSidebar - Desktop sidebar (returns null on mobile)
 */
export function EvidencePanelContent({ onClose }: EvidencePanelContentProps = {}) {
  const { t } = useTranslations();
  const intl = useIntl();
  const {
    searchQuery,
    setSearchQuery,
    chatToDelete,
    setChatToDelete,
    showSignUpModal,
    setShowSignUpModal,
    isAuthenticated,
    currentChatId,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isDeleting,
    streamingChatIds,
    chats,
    groupedChats,
    filteredSavedChats,
    savedChatsPending,
    handleSelectChat,
    handleDeleteChatClick,
    handleSaveChatClick,
    handleUnsaveChatClick,
    handleDeleteConfirm,
    isChatSaved,
    editingState,
    editingTitle,
    setEditingTitle,
    handleStartRename,
    handleSubmitRename,
    handleCancelRename,
  } = useEvidencePanelState({ onClose });

  const renderChatItem = (chat: ChatListItem, section: 'saved' | 'recent' | 'older') => (
    <EvidenceChatItem
      chat={chat}
      section={section}
      isSelected={chat.id === currentChatId}
      isStreaming={streamingChatIds.has(chat.id)}
      isSaved={isChatSaved(chat.id)}
      showSaveButton={isAuthenticated}
      currentChatId={currentChatId}
      onSelect={handleSelectChat}
      onSave={handleSaveChatClick}
      onUnsave={handleUnsaveChatClick}
      onDelete={handleDeleteChatClick}
      editingTitle={editingTitle}
      onEditingTitleChange={setEditingTitle}
      onRenameRequest={() => handleStartRename(chat, section)}
      onRenameSubmit={handleSubmitRename}
      onRenameCancel={handleCancelRename}
      isEditing={editingState?.chatId === chat.id && editingState?.section === section}
    />
  );

  const renderChatList = (
    chatList: ChatListItem[],
    section: 'saved' | 'recent' | 'older'
  ) => (
    <div className="flex flex-col gap-1">
      {chatList.map((chat) => (
        <div key={chat.id}>{renderChatItem(chat, section)}</div>
      ))}
    </div>
  );

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Search - New Chat action is now in the MobileSidebar header */}
        <div className="p-4">
          <SearchInput
            placeholder={t('chat.history.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!isAuthenticated}
            clearAriaLabel={intl.formatMessage({
              defaultMessage: 'Clear search',
              id: '4YJHutR26p',
            })}
            title={!isAuthenticated ? t('chat.history.signUpToSearch') : undefined}
          />
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <TypographyP2 className="text-text-secondary">
                {t('chat.history.loading')}
              </TypographyP2>
            </div>
          ) : chats.length === 0 && filteredSavedChats.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <TypographyP2 className="text-text-secondary">
                {searchQuery ? t('chat.history.noResults') : t('chat.history.empty')}
              </TypographyP2>
            </div>
          ) : (
            <div className="space-y-4">
              {isAuthenticated && !savedChatsPending && filteredSavedChats.length > 0 && (
                <div>
                  <TypographyCaption className="mb-2 text-text-secondary">
                    {intl.formatMessage({
                      defaultMessage: 'Pinned',
                      id: 'Wmnz4ilR8l',
                      description: 'Evidence panel section heading for pinned chats',
                    })}
                  </TypographyCaption>
                  {renderChatList(filteredSavedChats, 'saved')}
                </div>
              )}

              {groupedChats.recent.length > 0 && (
                <div>
                  <TypographyCaption className="mb-2 text-text-secondary">
                    {t('chat.history.recent')}
                  </TypographyCaption>
                  {renderChatList(groupedChats.recent, 'recent')}
                </div>
              )}
              {groupedChats.older.length > 0 && (
                <div>
                  <TypographyCaption className="mb-2 text-text-secondary">
                    {t('chat.history.older')}
                  </TypographyCaption>
                  {renderChatList(groupedChats.older, 'older')}
                </div>
              )}
              {hasNextPage && (
                <div className="px-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start px-2"
                    onClick={() => {
                      void fetchNextPage();
                    }}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <Loader size={14} strokeWidth={2} />
                    ) : (
                      t('chat.contextLinking.loadMore')
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DeleteChatDialog
        open={!!chatToDelete}
        isDeleting={isDeleting}
        onCancel={() => setChatToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />

      <SignUpPromptModalChat
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        title={t('chat.history.signUpTitle')}
        subtitle={t('chat.history.signUpSubtitle')}
      />
    </>
  );
}
